import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { isAuthError } from "@/lib/api";
import { authenticatedDownload, smartSaveOrShare } from "@/lib/download";
import { usePdfViewer } from "@/lib/PdfViewerContext";
import { CertificatePreview, ReceiptPreview } from "@/components/DocumentPreview";
import { useAuth } from "@/lib/AuthContext";
import { useAccounts } from "@/lib/AccountContext";
import { AccountSwitcher } from "@/components/AccountSwitcher";
import { FileText, Download, FileBadge2, Calendar, ShieldCheck, Eye, Loader2 } from "lucide-react";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";

export default function Documents() {
  const { t, i18n } = useTranslation();
  const { openPdf } = usePdfViewer();
  const { user } = useAuth();
  const { selectedAccount } = useAccounts();
  const acc = selectedAccount;
  const [txs, setTxs] = useState([]);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    if (!acc?.id) { setTxs([]); return; }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/transactions", { params: { limit: 20, account_id: acc.id } });
        if (alive) setTxs(data);
      } catch (e) {
        if (!isAuthError(e)) toast.error(t("documents.load_error"));
      }
    })();
    return () => { alive = false; };
  }, [acc?.id]);

  const lp = () => `?lang=${(i18n.language || "es").split("-")[0]}${acc?.id ? `&account_id=${acc.id}` : ""}`;
  const downloadCert = async () => {
    if (downloading) return;
    setDownloading("cert");
    try { await smartSaveOrShare(`/documents/certificate${lp()}`, "certificado_cuenta.pdf"); }
    finally { setDownloading(null); }
  };
  const viewCert = () => openPdf({
    title: t("documents.cert_title"),
    content: <CertificatePreview account={acc} user={user} />,
    path: `/documents/certificate${lp()}`,
    filename: "certificado_cuenta.pdf",
  });
  const downloadReceipt = async (id, ref) => {
    if (downloading) return;
    const filename = `comprobante_${(ref || id).replace(/[^A-Za-z0-9_-]/g, "_")}.pdf`;
    setDownloading(id);
    try { await smartSaveOrShare(`/documents/receipt/${id}${lp()}`, filename); }
    finally { setDownloading(null); }
  };
  const viewReceipt = (tx) => openPdf({
    title: `${t("documents.receipt_word")} ${tx.reference || ""}`.trim(),
    content: <ReceiptPreview tx={tx} account={acc} />,
    path: `/documents/receipt/${tx.id}${lp()}`,
    filename: `comprobante_${(tx.reference || tx.id).replace(/[^A-Za-z0-9_-]/g, "_")}.pdf`,
  });

  return (
    <div className="space-y-6 max-w-7xl" data-testid="documents-page">
      <div>
        <div className="font-mono-fin text-xs uppercase tracking-widest text-blue-600">{t("documents.eyebrow")}</div>
        <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter text-slate-900 mt-1">{t("documents.title")}</h1>
        <p className="text-slate-500 mt-1">{t("documents.subtitle")}</p>
      </div>

      <AccountSwitcher />

      <div className="grid lg:grid-cols-2 gap-5">
        <div
          className="rounded-3xl p-5 sm:p-7 text-white relative overflow-hidden glow-purple"
          style={{ background: "linear-gradient(135deg, #0B0F2F 0%, #312E81 30%, #6D28D9 65%, #2563EB 100%)" }}
          data-testid="cert-card"
        >
          <div className="absolute -right-16 -top-16 w-64 h-64 border border-white/10 rounded-full"></div>
          <div className="absolute right-10 -bottom-10 w-32 h-32 border border-white/15 rounded-full"></div>
          <FileBadge2 size={32} className="text-purple-200 relative" />
          <div className="font-display font-bold text-xl sm:text-2xl mt-5 relative">{t("documents.cert_title")}</div>
          <p className="text-purple-100 mt-2 text-sm relative">{t("documents.cert_lead")}</p>
          <div className="mt-6 space-y-1.5 text-xs font-mono-fin text-purple-100 relative">
            <div className="break-words"><span className="text-purple-300">{t("overview.holder")}</span> · {acc?.holder_name}</div>
            <div className="break-words"><span className="text-purple-300">{t("documents.row_bank")}</span> · {acc?.bank_name || "OpenPayments S.A."}</div>
            <div className="break-all"><span className="text-purple-300">{t("overview.account_number")}</span> · {acc?.iban ? acc.iban : t("overview.account_number_chat")}</div>
          </div>
          <div className="mt-7 flex flex-wrap gap-3 relative">
            <button onClick={downloadCert}
                    disabled={downloading === "cert"}
                    className="inline-flex items-center gap-2 bg-white text-purple-700 px-5 sm:px-6 py-3 rounded-full font-semibold hover:bg-purple-50 transition-colors text-sm disabled:opacity-60"
                    data-testid="download-cert-btn">
              {downloading === "cert" ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} {t("documents.download_pdf")}
            </button>
            <button onClick={viewCert}
                    className="inline-flex items-center gap-2 bg-white/10 text-white border border-white/30 px-5 sm:px-6 py-3 rounded-full font-semibold hover:bg-white/20 transition-colors text-sm"
                    data-testid="view-cert-btn">
              <Eye size={16} /> {t("documents.view")}
            </button>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-3xl p-5 sm:p-7">
          <FileText size={32} className="text-blue-600" />
          <div className="font-display font-bold text-xl sm:text-2xl mt-5 text-slate-900">{t("documents.recent_title")}</div>
          <p className="text-slate-500 mt-2 text-sm">{t("documents.recent_lead")}</p>
          <div className="mt-5 divide-y divide-slate-100 max-h-80 overflow-y-auto">
            {txs.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">{t("documents.no_movements")}</div>}
            {txs.map((tx) => (
              <div key={tx.id} className="py-3 flex items-center gap-2 sm:gap-3">
                <span className="w-9 h-9 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center shrink-0"><Calendar size={14} /></span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{tx.description}</div>
                  <div className="text-[11px] font-mono-fin text-slate-500 truncate">
                    {tx.reference} · {fmtDateTime(tx.executed_at || tx.created_at)}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => viewReceipt(tx)} title={t("documents.view")}
                          className="text-slate-500 hover:text-blue-600 hover:bg-blue-50 px-2.5 py-1.5 rounded-full text-xs inline-flex items-center gap-1.5"
                          data-testid={`doc-view-${tx.id}`}>
                    <Eye size={14} /> <span className="hidden sm:inline">{t("documents.view")}</span>
                  </button>
                  <button onClick={() => downloadReceipt(tx.id, tx.reference)} disabled={downloading === tx.id} className="text-blue-600 hover:bg-blue-50 px-2.5 py-1.5 rounded-full text-xs inline-flex items-center gap-1.5 font-semibold disabled:opacity-50"
                          data-testid={`doc-download-${tx.id}`}>
                    {downloading === tx.id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} PDF
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 flex items-start gap-3 text-sm text-slate-600">
        <ShieldCheck size={20} className="text-blue-600 shrink-0 mt-0.5" />
        <span>
          <b className="text-slate-900">{t("documents.legal_validity")}</b> {t("documents.legal_text")}{" "}
          <a href="/terminos#documentos" className="text-blue-600 hover:underline">{t("documents.legal_link")}</a>.
        </span>
      </div>
    </div>
  );
}
